import { captureException } from '@sentry/react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { useSetAtom } from 'jotai';
import { getInventoryArticleType, nextQuantity } from '@web/utils/src';
import arrowTop from '@web/utils/src/assets/icons/functions/arrow-top.svg';
import { setCartItemQuantityAtom } from '@/store/store';
import { OrderDelivery, OrderItem } from '@/store/types';

export interface QuantityInputProps {
  item: OrderItem;
  delivery: OrderDelivery;
}

const QuantityInput = (props: QuantityInputProps) => {
  const { item, delivery } = props;
  const { retailerId = '1' } = useParams();
  const [initialValue, setInitialValue] = useState<number>(item.quantity || 0);
  const setCartItemQuantity = useSetAtom(setCartItemQuantityAtom);
  const [inputValue, setInputValue] = useState<number>(item.quantity || 0);

  const itemType = useMemo(() => {
    return getInventoryArticleType(item);
  }, [item]);

  const changeItemAmount = (type: 'increment' | 'decrement') => {
    if ((inputValue < 1 && type === 'decrement') || !item) return;
    if (itemType === 'loose' && item.amount) {
      const deliveryOptions = item.quantityOptions?.delivery;
      if (!deliveryOptions || !deliveryOptions.length) {
        captureException(
          new Error(
            `quantity options not defined on item #${item.inventoryID}`,
          ),
        );
      }
      const newAmount = nextQuantity(
        deliveryOptions as string[],
        inputValue,
        type,
      );

      if (!newAmount) return;

      if (type === 'increment') {
        setInputValue(newAmount);
      } else {
        setInputValue((prev) => prev - newAmount);
      }
    } else {
      type === 'increment'
        ? setInputValue((prev) => prev + 1)
        : setInputValue((prev) => prev - 1);
    }
  };

  const handleActualization = () => {
    if (!item) return;
    setCartItemQuantity({
      retailerId,
      deliveryId: delivery.id,
      catalogueId: item.catalogueID,
      quantity: inputValue,
    });
    setInitialValue(inputValue);
  };

  return (
    <div
      className={clsx(
        itemType !== 'loose' && 'justify-between',
        'flex border border-primary-grey rounded-8-0 px-1-5 py-1-3 min-w-[150px] max-w-[175px] relative',
      )}
    >
      <div className="w-1/2 flex">
        <div className="min-w-[20px] flex flex-col items-center justify-center">
          <img
            src={arrowTop}
            className="w-1-0 h-1-0 mb-0-2 cursor-pointer"
            onClick={() => changeItemAmount('increment')}
          />
          <img
            src={arrowTop}
            className={clsx(
              inputValue === 0 ? 'opacity-30' : 'cursor-pointer',
              'w-1-0 h-1-0 transform rotate-180',
            )}
            onClick={() => changeItemAmount('decrement')}
          />
        </div>
        {itemType !== 'loose' && (
          <input
            value={inputValue}
            type="number"
            readOnly
            onChange={(e) => setInputValue(e.target.valueAsNumber)}
            className="w-5-0 mb-0 block appearance-none bg-primary-white pl-2-0 font-md text-2-5 md:text-1-6 leading-1-0 text-primary-black focus:outline-none"
          />
        )}
      </div>
      <div className="flex items-center w-full">
        <span className="text-primary-dark text-body1 leading-1-0 font-li">
          /
          {itemType === 'countable' &&
            (item?.amount || 1) * (item?.quantity || 1)}
        </span>
        <span className={clsx('text-body1 capitalize font-li')}>
          {itemType !== 'loose'
            ? item.unit
            : `${(item.amount || 1) * inputValue} ${item.unit} `}
        </span>
      </div>
      {initialValue !== inputValue && (
        <p
          className="text-primary-green text-2-0 md:text-1-2 leading-1-0 underline cursor-pointer absolute -bottom-2-2 md:-bottom-1-5"
          onClick={handleActualization}
        >
          Aktualisieren
        </p>
      )}
    </div>
  );
};

export default QuantityInput;
